import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScanFace, Loader2, Mail, Phone, ShieldCheck, Fingerprint } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import ContactRow from "components/ContactRow";
import { useI18n } from "i18n";
import LanguageSwitcher from "components/LanguageSwitcher";
import portalApi, { clearPortalToken } from "lib/portalApi";

// /pub — the person's own area. Read-only for now: identity, proven contacts and the state of
// their biometric. Editing contacts from here rides on the same code flow and comes next.
export default function PortalHome() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [loading, setLoading] = useState(true);

  // Contacts come from their own endpoint — no account id travels in the URL; the backend
  // resolves the subject from the token.
  const loadContacts = useCallback(async () => {
    try { setContacts((await portalApi.get("/contacts/")).data); } catch { /* keep the old view */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await portalApi.get("/me/");
      if (r.data.face_required) { navigate("/pub/enroll", { replace: true }); return; }
      setMe(r.data);
    } catch {
      clearPortalToken();
      navigate("/pub/login", { replace: true });
    } finally { setLoading(false); }
  }, [navigate]);
  useEffect(() => { load(); loadContacts(); }, [load, loadContacts]);

  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }
  const u = me?.user || {};

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <ScanFace className="h-6 w-6 text-primary" />
          <span className="font-semibold tracking-tight">SecFace</span>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted-foreground">{u.first_name || u.email}</span>
          <LanguageSwitcher />
          <button onClick={() => { clearPortalToken(); navigate("/pub/login", { replace: true }); }}
            className="text-muted-foreground hover:text-foreground">{t("pub.signOut", "Sair")}</button>
        </div>
      </header>

      <main className="flex-1 px-6 py-8 max-w-2xl w-full mx-auto">
        <h1 className="text-2xl font-bold tracking-tight mb-1">{t("pub.home.title", "Meus dados")}</h1>
        <p className="text-sm text-muted-foreground mb-6">
          {t("pub.home.subtitle", "Os dados que provamos pertencerem a você.")}
        </p>

        <Card className="mb-4">
          <CardContent className="pt-5">
            <ContactRow kind="email" icon={Mail} t={t} client={portalApi} base="/contacts"
              label={t("pub.email", "E-mail")} value={contacts?.email} masked={contacts?.email}
              verified={contacts?.email_verified} onDone={loadContacts} />
            <ContactRow kind="phone" icon={Phone} t={t} client={portalApi} base="/contacts"
              label={t("pub.phone", "Celular")} value={contacts?.phone} masked={contacts?.phone}
              verified={contacts?.phone_verified} onDone={loadContacts} />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-3">
              <Fingerprint className="h-4 w-4 text-primary shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium">{t("pub.biometric", "Minha biometria")}</div>
                <div className="text-xs text-muted-foreground font-mono truncate">{me?.person_id}</div>
              </div>
              <span className="inline-flex items-center gap-1 text-xs text-emerald-500">
                <ShieldCheck className="h-3.5 w-3.5" />{t("pub.enrolled", "Cadastrada")}
              </span>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
